<app-page-header [title]="'Vehicle Model Details'"
                 [subtitle]="'View details of the selected vehicle model'"></app-page-header>
<div class="mb-6">

    <div class="flex gap-2 items-center mb-2">
        <h2 class="text-xl">{{ vehicleModel?.label }}</h2>
    </div>
    <p><strong>ID:</strong> {{ vehicleModel?.id }}</p>
    <p><strong>Label:</strong> {{ vehicleModel?.label }}</p>
    <p><strong>Model Series: </strong> {{ vehicleModel?.modelSeries }}</p>
    <p><strong>Brand:</strong> {{ vehicleModel?.brand?.label || 'Unknown' }}</p>
    <p><strong>Body Type:</strong> {{ vehicleModel?.bodyType?.label || 'Unknown' }}</p>
    <p><strong>Car Class:</strong> {{ vehicleModel?.carClass?.label || 'Unknown' }}</p>
    <p><strong>Created By:</strong> {{ vehicleModel?.createdBy | roleName }}</p>
    <p><strong>Updated By:</strong> {{ vehicleModel?.updatedBy | roleName }}</p>
    @if (vehicleModel?.image) {
        <div>
            <p class="mb-2"><strong>Image:</strong></p>
            <p-image
                    [src]="vehicleModel?.image?.content?.cloudfrontUrl || 'assets/images/no-image.png'"
                    alt="Sample Image"
                    preview="true"
                    width="250"
            ></p-image>
    </div>
    }

</div>
<h2 class="text-2xl">Samples</h2>
<p class="text-gray-500 mb-2">Samples associated with this vehicle model</p>
<div class="border border-gray-300 dark:border-gray-700 p-4 rounded-md mb-6">

    @if (vehicleModel?.samples && vehicleModel?.samples?.length) {
        <div class="border border-gray-300 dark:border-gray-700 p-2 dark:p-0 mb-5 rounded-sm overflow-x-auto"
    >
        <p-table
            #samplesTable
            [value]="vehicleModel?.samples ?? []"
            [tableStyle]="{'min-width': '75rem'}"
            [paginator]="true"
            [rows]="5"
            [rowsPerPageOptions]="[5, 10, 20]"
            [globalFilterFields]="['sample.printedName', 'sample.es2Key', 'sample.fieldOfUse', 'sample.type.name', 'sample.category.name', 'sample.state', 'salesCodes']"
            [sortMode]="'multiple'"
        >
            <!-- Global Search -->
            <ng-template pTemplate="caption">
                <div class="flex justify-between">
                    <p-iconfield iconPosition="left">
                        <p-inputicon>
                            <i class="pi pi-search"></i>
                        </p-inputicon>
                        <input
                                pInputText
                                type="text"
                                placeholder="Search"
                                (input)="onGlobalSearch($event)"
                        />
                    </p-iconfield>
                </div>
            </ng-template>
            <ng-template pTemplate="header">
                <tr>
                    <th pSortableColumn="sample.printedName">Printed Name
                        <p-sortIcon field="sample.printedName"/>
                    </th>
                    <th pSortableColumn="sample.es2Key">ES2Key
                        <p-sortIcon field="sample.es2Key"/>
                    </th>
                    <th pSortableColumn="sample.fieldOfUse">Field of Use
                        <p-sortIcon field="sample.fieldOfUse"/>
                    </th>
                    <th pSortableColumn="sample.type.name">Type
                        <p-sortIcon field="sample.type.name"/>
                    </th>
                    <th pSortableColumn="sample.category.name">Category
                        <p-sortIcon field="sample.category.name"/>
                    </th>
                    <th pSortableColumn="sample.state">State
                        <p-sortIcon field="sample.state"/>
                    </th>
                    <th pSortableColumn="sample.lastActivity">Last Activity
                        <p-sortIcon field="sample.lastActivity"/>
                    </th>
                    <th>Available / Expiration</th>
                    <th>Branding</th>
                    <th pSortableColumn="salesCodes">Sales Codes
                        <p-sortIcon field="salesCodes"/>
                    </th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-sample>
                <tr>
                    <td>
                        <div>{{ sample?.sample?.printedName }}</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">
                            {{ sample?.sample?.detailNames || '' }}
                        </div>
                    </td>
                    <td>{{ sample?.sample?.es2Key }}</td>
                    <td>
                        <p-tag
                                [value]="sample?.sample?.fieldOfUse | enumLabel"
                                [severity]="sample?.sample?.fieldOfUse | fieldOfUseSeverity"
                        ></p-tag>
                    </td>
                    <td>
                        <p-tag
                                [value]="sample?.sample?.type.name"
                                severity="secondary"
                        ></p-tag>
                    </td>
                    <td>
                        <p-tag
                                [value]="sample?.sample?.category?.name ?? 'No category'"
                                severity="secondary"
                        ></p-tag>
                    </td>
                    <td>
                        <p-tag
                                [value]="sample?.sample?.state | enumLabel"
                                [severity]="sample?.sample?.state | sampleStateSeverity"
                        ></p-tag>
                    </td>
                    <td>{{ sample?.sample?.lastActivity | formatDateTime }}</td>
                    <td>{{ sample.availabilityDate | formatDate }} {{ sample.expirationDate | formatDate }}</td>
                    <td>
                        <p-select
                                isMaintainer
                                [options]="brandingOptions"
                                [(ngModel)]="sample.sampleBranding"
                                (onChange)="onChangeBranding($event.value, sample)"
                                placeholder="Select branding"
                                optionLabel="label"
                                optionValue="value"
                                styleClass="w-full"
                                appendTo="body"
                        ></p-select>
                    </td>
                    <td class="whitespace-normal break-words max-w-[200px]">
                        {{ sample.salesCodes }}
                    </td>
                    <td>
                        <div class="flex justify-end gap-2">
                            <p-button severity="primary" text icon="pi pi-eye"
                                      pTooltip="View sample"
                                      tooltipPosition="left"
                                      [routerLink]="'/samples/sample/' + sample.sample.id"></p-button>
                            <p-button icon="pi pi-barcode" text
                                      pTooltip="Change sales codes"
                                      tooltipPosition="left"
                                      isMaintainer
                                      [hidden]="false"
                                      (click)="openChangeSalesCodesDialog(sample)"></p-button>
                            <p-button icon="pi pi-clock" text
                                      pTooltip="Change vehicle model availability"
                                      tooltipPosition="left"
                                      isMaintainer
                                      [hidden]="false"
                                      (click)="openChangeVehicleModelAvailabilityDialog(sample)"></p-button>
                            <p-button severity="danger" text icon="pi pi-trash"
                                      isMaintainer
                                      pTooltip="Remove sample"
                                      tooltipPosition="left"
                                      (click)="removeSample(sample.sample)"></p-button>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    }
    <p-button label="Assign Sample" icon="pi pi-plus" (click)="openAddSampleDialog()" [hidden]="false"
              isMaintainer></p-button>
</div>

<div class="flex justify-between gap-2">
    <div>
        <p-button (click)="openEditDialog()" icon="pi pi-pencil" isAdmin label="Edit" severity="secondary"
                  text></p-button>
        <p-button (click)="confirmDelete()" icon="pi pi-trash" isAdmin label="Delete" severity="danger" text></p-button>
    </div>
    <p-button label="Back to Vehicle Models" icon="pi pi-arrow-left" routerLink="/vehicle-models"></p-button>
</div>

<!-- Update Dialog -->
<app-update-vehicle-model-dialog
        [(visible)]="displayUpdateDialog"
        [vehicleModel]="editableVehicleModel"
        (vehicleModelUpdated)="reloadVehicleModelDetails()"
></app-update-vehicle-model-dialog>

<!-- Add Sample Dialog -->
<app-add-samples-to-vehicle-model-dialog
        [(visible)]="displayAddSampleDialog"
        [vehicleModelId]="vehicleModel?.id ?? 0"
        (samplesAdded)="reloadVehicleModelDetails()"></app-add-samples-to-vehicle-model-dialog>

<app-change-sample-vehicle-model-availability-dialog
        [(visible)]="displayChangeVehicleModelAvailabilityDialog"
        [sample]="this.selectedSample"
        [vehicleModel]="this.vehicleModel"
        (availabilityChange)="reloadVehicleModelDetails()"
></app-change-sample-vehicle-model-availability-dialog>

<app-change-sales-codes-dialog
        [(visible)]="displayChangeSalesCodesDialog"
        [sample]="this.selectedSample"
        [vehicleModel]="this.vehicleModel"
        (salesCodesChange)="reloadVehicleModelDetails()"
></app-change-sales-codes-dialog>

